import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Confirm, Icons, Modal } from '@components/common';
import ProfileTabSection from '@components/common/ProfileTabSection';
import Table from '@components/common/Table/Table';
import EditChanges from '@components/routes/AdminPanel/ManageNetworkElementConfigurations/ChangeMonitoring/EditChanges';
import ViewChanges from '@components/routes/AdminPanel/ManageNetworkElementConfigurations/ChangeMonitoring/ViewCanges';

import { useChangeMonitoringFilters } from '@hooks/networkElementConfigurations/useChangeMonitoringFilters';
import useInvalidateChangeMonitoring from '@hooks/networkElementConfigurations/useInvalidateChangeMonitoring';
import useMutateChangeMonitoringCancelAllAccepted from '@hooks/networkElementConfigurations/useMutateCancelAllAccepted';
import useMutateChangeMonitoringCancel from '@hooks/networkElementConfigurations/useMutateCancelChangeMonitoring';
import useMutateChangeMonitoringErrorMessage
	from '@hooks/networkElementConfigurations/useMutateChangeMonitoringErrorMessage';
import useMutateChangeMonitoringCommitAllAccepted
	from '@hooks/networkElementConfigurations/useMutateCommitAllAcceptedChangeMonitoring';
import useMutateChangeMonitoringCommit from '@hooks/networkElementConfigurations/useMutateCommitChangeMonitoring';
import useMutateChangeMonitoringTryAgain from '@hooks/networkElementConfigurations/useMutateTryAgainChangeMonitoring';
import { useQueryChangeMonitoringActive } from '@hooks/networkElementConfigurations/useQueryChangeMonitoring';
import { useQueryChangeMonitoringCard } from '@hooks/networkElementConfigurations/useQueryChangeMonitoringCard';
import { useQueryChangeMonitoringHistory } from '@hooks/networkElementConfigurations/useQueryChangeMonitoringHistory';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { ISortBy, ITablePagination, ITableSearch, ITableSort, TTableFilter } from '@interfaces/components/table';
import {
	INetworkElementConfigurationsFilter,
} from '@interfaces/networkElementConfigurations/dto/networkElementConfigurationsDTO';
import { IChangeMonitoring } from '@interfaces/networkElementConfigurations/networkElementConfigurations';
import { IChangeMonitoringTableState } from './interfaces';

import { defaultMeta } from '@enums/common';

import { MODAL_WIDTH_580 } from '@constants/modals';
import { routes } from '@constants/routes';
import {
	CANCEL_ALL_PARAMETERS_QUESTION,
	CANCEL_PARAMETER_QUESTION,
	CANCEL_PARAMETER_QUESTION_WITH_FILTER,
	CHANGE_MONITORING_TABS,
	CHANGE_MONITORING_TABS_NAMES,
	DEFAULT_TABS,
	EChangeMonitorActions,
	RESEND_SINGLE_PARAMETER_QUESTION,
	RESEND_SINGLE_PARAMETER_SUBTEXT,
	SEND_ALL_PARAMETER_QUESTION,
	SEND_ALL_PARAMETER_QUESTION_FILTER,
	SEND_ALL_PARAMETER_SUBTEXT,
	SEND_SINGLE_PARAMETER_QUESTION,
	SEND_SINGLE_PARAMETER_SUBTEXT,
	TChangeMonitorAction,
} from './constants';

import { getAlarmFilterValues } from '../../AlarmManagement/AlarmManagementByVendor/utils';
import { generateAlarmsFilters } from '../../Alarms/AlarmByVendor/utils';
import NetworkConfigurationsNotes from '../NetworkConfigurationsNotes';
import ChangeMonitoringHistoryStatuses from './ChangeMonitoringHistoryStatuses';
import { getChangeMonitoringColumns } from './utils';

import styles from './ChangeMonitoring.module.scss';

const ChangeMonitoring = () => {
	const {
		accessPermission: {
			networkOperatorCmModuleAdmin,
			networkOperatorCmModuleEditor,
		},
	} = useAppSelector(auth);

	const navigate = useNavigate();

	const { id: networkElementId } = useParams();

	const [selectedConfiguration, setSelectedConfiguration] = useState<IChangeMonitoring | null>(null);
	const [currentTab, setCurrentTab] = useState(CHANGE_MONITORING_TABS_NAMES.activeChanges);
	const [tableState, setTableState] = useState<IChangeMonitoringTableState>(DEFAULT_TABS);

	const [isEditMode, setIsEditMode] = useState(false);
	const [isHistoryOpen, setIsHistoryOpen] = useState(false);
	const [isStatusConfirmationOpen, setIsStatusConfirmationOpen] = useState(false);
	const [isOpenViewModal, setIsOpenViewModal] = useState(false);
	const [isErrorModal, setIsErrorModal] = useState(false);
	const [isOpenNoteModal, setIsOpenNoteModal] = useState(false);
	const [isDirtyNotesForm, setIsDirtyNotesForm] = useState(false);

	const [action, setAction] = useState<TChangeMonitorAction | null>(null);

	const {
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
	} = tableState[currentTab];

	const isActiveTab = currentTab === CHANGE_MONITORING_TABS_NAMES.activeChanges;

	const isViewerOnly = !networkOperatorCmModuleEditor && !networkOperatorCmModuleAdmin;

	const { invalidateAlarmsChangeMonitoring } = useInvalidateChangeMonitoring();

	const { filtersList, isLoadingFilters } = useChangeMonitoringFilters({
		networkElementId,
		isActive: isActiveTab,
	});

	const {
		cancelAllAccepted,
		isLoadingCancelAllAccepted,
	} = useMutateChangeMonitoringCancelAllAccepted(invalidateAlarmsChangeMonitoring);

	const {
		commitAllAcceptedChanges,
		isLoadingCommitAllAcceptedChangeMonitoring,
	} = useMutateChangeMonitoringCommitAllAccepted(invalidateAlarmsChangeMonitoring);

	const {
		changeMonitoringCard,
		isLoadingChangeMonitoringCard,
	} = useQueryChangeMonitoringCard({ networkElementId });

	const {
		getErrorMessage,
		isLoadingChangeMonitoringErrorMessage,
		errorMessage,
	} = useMutateChangeMonitoringErrorMessage();

	const {
		cancelChange,
		isLoadingCancelChangeMonitoring,
	} = useMutateChangeMonitoringCancel(invalidateAlarmsChangeMonitoring);

	const {
		commitChange,
		isLoadingCommitChangeMonitoring,
	} = useMutateChangeMonitoringCommit(invalidateAlarmsChangeMonitoring);

	const {
		tryAgainChange,
		isLoadingTryAgainChangeMonitoring,
	} = useMutateChangeMonitoringTryAgain(invalidateAlarmsChangeMonitoring);

	const groupNumberSortDirection = sortFieldName === 'groupNumber' ? sortDirection : undefined;

	const filterValues = getAlarmFilterValues(tableState[currentTab]?.filter);

	const {
		refetchChangeMonitoring,
		changeMonitoringActive,
		isLoadingChangeMonitoringActive,
		countOfRecords,
	} = useQueryChangeMonitoringActive({
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		networkElementId,
		searchValue,
		groupNumberSortDirection,
		isActive: isActiveTab,
		...filterValues,
	});

	const {
		changeMonitoringHistory,
		isLoadingChangeMonitoringHistory,
		countOfRecords: countOfChangeLogRecords,
	} = useQueryChangeMonitoringHistory({
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		networkElementId,
		searchValue,
		groupNumberSortDirection,
		isActive: !isActiveTab,
		...filterValues,
	});

	const openNoteModal = useCallback((item: IChangeMonitoring) => {
		setSelectedConfiguration(item);
		setIsOpenNoteModal(true);
	}, []);

	const closeNoteModal = useCallback(() => {
		setIsOpenNoteModal(false);
		setIsDirtyNotesForm(false);
		setSelectedConfiguration(null);
	}, []);

	const onBackClickHandler = useCallback(() => {
		navigate(`${routes.adminPanel.index}${routes.cmModule.networkElementConfigurations}`);
	}, [navigate]);

	const openViewModal = useCallback(async (item: IChangeMonitoring) => {
		setSelectedConfiguration(item);
		setIsOpenViewModal(true);
	}, []);

	const closeViewModal = useCallback(() => {
		setIsOpenViewModal(false);
		setSelectedConfiguration(null);
	}, []);

	const openEditModal = useCallback(async (item: IChangeMonitoring) => {
		setSelectedConfiguration(item);
		setIsEditMode(true);
	}, []);

	const closeEditModal = useCallback(() => {
		setIsEditMode(false);
		setSelectedConfiguration(null);
		void refetchChangeMonitoring();
	}, [refetchChangeMonitoring]);

	const closeStatusConfirmationModal = useCallback(() => {
		setIsStatusConfirmationOpen(false);
		setAction(null);
		setSelectedConfiguration(null);
	}, []);

	const openStatusConfirmationModal = useCallback((item: IChangeMonitoring, actionType: TChangeMonitorAction) => {
		if (!item.status) {
			return;
		}

		setIsStatusConfirmationOpen(true);
		setSelectedConfiguration(item);
		setAction(actionType);
	}, []);

	const openHistoryModal = useCallback((item: IChangeMonitoring) => {
		setSelectedConfiguration(item);
		setIsHistoryOpen(true);
	}, []);

	const closeHistoryModal = useCallback(() => {
		setIsHistoryOpen(false);
	}, []);

	const openErrorMessageModal = useCallback((item: IChangeMonitoring) => {
		setIsErrorModal(true);
		getErrorMessage({ updateId: item.id });
		setSelectedConfiguration(item);
	}, [setIsErrorModal, getErrorMessage]);

	const closeErrorMessageModal = useCallback(() => {
		setIsErrorModal(false);
		setSelectedConfiguration(null);
	}, []);

	const columns = useMemo(() => getChangeMonitoringColumns({
		currentTab,
		isViewerOnly,
		openHistoryModal,
		openErrorMessageModal,
		openStatusConfirmationModal,
		openViewModal,
		openEditModal,
		openNoteModal,
	}), [
		isViewerOnly,
		currentTab,
		openHistoryModal,
		openErrorMessageModal,
		openStatusConfirmationModal,
		openViewModal,
		openEditModal,
		openNoteModal,
	]);

	const clearPagination = useCallback(() => {
		setTableState((prevState) => ({
			...prevState,
			[currentTab]: {
				...prevState[currentTab],
				page: defaultMeta.page,
				pageSize: defaultMeta.pageSize,
			},
		}));
	}, [setTableState, currentTab]);

	const clearTableState = useCallback(() => {
		setTableState(DEFAULT_TABS);
	}, [setTableState]);

	const setFilters = useCallback((value: Record<string, any>) => {
		clearPagination();
		setTableState((prevState) => ({
			...prevState,
			[currentTab]: {
				...prevState[currentTab],
				filter: {
					...prevState[currentTab].filter,
					...value,
				},
			},
		}));
	}, [currentTab, setTableState, clearPagination]);

	const searchHandler = useCallback((searchValue: string) => {
		setTableState((prevState) => ({
			...prevState,
			[currentTab]: {
				...prevState[currentTab],
				searchValue,
				page: defaultMeta.page,
			},
		}));
	}, [setTableState, currentTab]);

	const pageHandler = useCallback((page: number) => {
		setTableState((prevState) => ({
			...prevState,
			[currentTab]: {
				...prevState[currentTab],
				page,
			},
		}));
	}, [currentTab]);

	const pageSizeHandler = useCallback((pageSize: number) => {
		setTableState((prevState) => ({
			...prevState,
			[currentTab]: {
				...prevState[currentTab],
				pageSize,
				page: defaultMeta.page,
			},
		}));
	}, [currentTab]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setTableState((prevState) => ({
			...prevState,
			[currentTab]: {
				...prevState[currentTab],
				sortDirection: sortBy[0]?.desc || false,
				sortFieldName: sortBy[0]?.id,
			},
		}));
	}, [currentTab]);

	const filters: TTableFilter[] = useMemo(() => {
		return generateAlarmsFilters(
			filtersList,
			tableState[currentTab].filter,
			setFilters,
		);
	}, [currentTab, tableState, setFilters, filtersList]);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords: isActiveTab ? countOfRecords : countOfChangeLogRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [
		page,
		pageSize,
		pageHandler,
		pageSizeHandler,
		countOfRecords,
		countOfChangeLogRecords,
		isActiveTab,
	]);

	const search: Array<ITableSearch> = useMemo(() => [{
		initialValue: searchValue,
		name: 'name',
		handler: searchHandler,
	}], [searchValue, searchHandler]);

	const isOpenFilters = useMemo(() => {
		const hasActiveFilters = !!Object.values(tableState[currentTab].filter).filter(Boolean).length;

		return tableState[currentTab].filter && hasActiveFilters || null;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tableState[currentTab].filter, currentTab]);

	useEffect(() => {
		clearTableState();
	}, [clearTableState]);

	const sendAllFilteredAcceptedToRunning = useCallback((filterValues?: INetworkElementConfigurationsFilter) => {
		return async () => {
			if (!networkElementId) return;

			await commitAllAcceptedChanges({
				networkElementId,
				...filterValues,
			});

			closeStatusConfirmationModal();
			void refetchChangeMonitoring();
		};
	}, [
		closeStatusConfirmationModal,
		commitAllAcceptedChanges,
		refetchChangeMonitoring,
		networkElementId,
	]);

	const abortCancelAllFilteredErrors = useCallback((filterValues?: INetworkElementConfigurationsFilter) => {
		return async () => {
			if (!networkElementId) return;

			await cancelAllAccepted({
				networkElementId,
				...filterValues,
			});

			closeStatusConfirmationModal();
			void refetchChangeMonitoring();
		};
	}, [
		cancelAllAccepted,
		closeStatusConfirmationModal,
		refetchChangeMonitoring,
		networkElementId,
	]);

	const handleSendAllAcceptedToRunning = useCallback(async () => {
		if (!networkElementId) return;

		setAction(EChangeMonitorActions.SEND_ALL_ACCEPTED);
		setSelectedConfiguration(null);
		setIsStatusConfirmationOpen(true);
	}, [networkElementId]);

	const handleCancelAllErrors = useCallback(() => {
		if (!networkElementId) return;

		setIsStatusConfirmationOpen(true);
		setAction(EChangeMonitorActions.CANCEL_ALL_ERRORS);
		setSelectedConfiguration(null);
	}, [networkElementId]);

	const handleTryAgainChange = useCallback(async () => {
		if (selectedConfiguration) {
			await tryAgainChange({ updateId: selectedConfiguration?.id });
		}

		closeStatusConfirmationModal();
		setSelectedConfiguration(null);
		void refetchChangeMonitoring();
	}, [
		closeStatusConfirmationModal,
		refetchChangeMonitoring,
		selectedConfiguration,
		tryAgainChange,
	]);

	const handleSendParameterToRunning = useCallback(() => {
		if (selectedConfiguration) commitChange({ updateId: selectedConfiguration.id });

		closeStatusConfirmationModal();
		setSelectedConfiguration(null);
		void refetchChangeMonitoring();
	}, [
		closeStatusConfirmationModal,
		commitChange,
		selectedConfiguration,
		refetchChangeMonitoring,
	]);

	const handleSingleParameterCancel = useCallback(() => {
		if (selectedConfiguration) cancelChange({ updateId: selectedConfiguration.id });

		closeStatusConfirmationModal();
		void refetchChangeMonitoring();
		setSelectedConfiguration(null);
	}, [
		cancelChange,
		closeStatusConfirmationModal,
		selectedConfiguration,
		refetchChangeMonitoring,
	]);

	const isFilterApplied = useMemo(() => {
		return filters.some(({ option }) => !!option);
	}, [filters]);

	const getConfirmationProps = useCallback(() => {
		switch (action) {
			case EChangeMonitorActions.TRY_AGAIN:
				return {
					question: RESEND_SINGLE_PARAMETER_QUESTION,
					children: RESEND_SINGLE_PARAMETER_SUBTEXT,
					onSubmit: handleTryAgainChange,
					onProceed: openViewModal,
					proceedBtnText: 'No, edit first',
					positiveBtnText: 'Yes, resend single',
				};
			case EChangeMonitorActions.SEND_ALL_ACCEPTED:
				return {
					question: isFilterApplied
						? SEND_ALL_PARAMETER_QUESTION_FILTER
						: SEND_ALL_PARAMETER_QUESTION,
					children: SEND_ALL_PARAMETER_SUBTEXT,
					onSubmit: sendAllFilteredAcceptedToRunning(isFilterApplied ? filterValues : undefined),
					onProceed: isFilterApplied ? sendAllFilteredAcceptedToRunning() : undefined,
					proceedBtnText: isFilterApplied ? 'No, send all instead' : undefined,
					positiveBtnText: isFilterApplied ? 'Yes, proceed' : undefined,
					negativeBtnText: isFilterApplied ? 'Cancel' : undefined,
				};
			case EChangeMonitorActions.SEND_TO_RUNNING:
				return {
					question: SEND_SINGLE_PARAMETER_QUESTION,
					children: SEND_SINGLE_PARAMETER_SUBTEXT,
					onSubmit: handleSendParameterToRunning,
					onProceed: handleSendAllAcceptedToRunning,
					proceedBtnText: 'No, apply all instead',
					positiveBtnText: 'Yes, proceed',
					negativeBtnText: 'Cancel',
				};
			case EChangeMonitorActions.CANCEL:
				return {
					question: CANCEL_PARAMETER_QUESTION,
					onSubmit: handleSingleParameterCancel,
				};
			case EChangeMonitorActions.CANCEL_ALL_ERRORS:
				return {
					question: isFilterApplied
						? CANCEL_PARAMETER_QUESTION_WITH_FILTER
						: CANCEL_ALL_PARAMETERS_QUESTION,
					onSubmit: abortCancelAllFilteredErrors(isFilterApplied ? filterValues : undefined),
					onProceed: isFilterApplied ? abortCancelAllFilteredErrors() : undefined,
					proceedBtnText: isFilterApplied ? 'No, cancel all instead' : undefined,
					positiveBtnText: isFilterApplied ? 'Yes, proceed' : undefined,
					negativeBtnText: isFilterApplied ? 'Cancel' : undefined,
				};
			default:
				return null;
		}
	}, [
		isFilterApplied,
		filterValues,
		action,
		abortCancelAllFilteredErrors,
		sendAllFilteredAcceptedToRunning,
		handleSendParameterToRunning,
		handleSingleParameterCancel,
		handleTryAgainChange,
		handleSendAllAcceptedToRunning,
		openViewModal,
	]);

	const confirmationProps = getConfirmationProps();

	const isLoading = isLoadingChangeMonitoringActive ||
		isLoadingChangeMonitoringHistory ||
		isLoadingCancelAllAccepted ||
		isLoadingCommitAllAcceptedChangeMonitoring ||
		isLoadingChangeMonitoringCard ||
		isLoadingFilters;

	const isLoadingConfirmation = isLoadingCancelChangeMonitoring ||
		isLoadingCommitChangeMonitoring ||
		isLoadingTryAgainChangeMonitoring ||
		isLoadingChangeMonitoringErrorMessage;

	const getCurrentTab = useCallback(() => {
		switch (currentTab) {
			case CHANGE_MONITORING_TABS_NAMES.activeChanges:
				return (
					<Table
						key={CHANGE_MONITORING_TABS_NAMES.activeChanges}
						columns={columns}
						customLeftAction={!isViewerOnly && (
							<div className={styles.customAction}>
								<Button
									onClick={handleSendAllAcceptedToRunning}
									secondary
									text='Send accepted to running'
								/>
								<Button
									onClick={handleCancelAllErrors}
									secondary
									text='Cancel errors'
								/>
							</div>
						)}
						data={changeMonitoringActive}
						filters={filters}
						isDeleteActionDisabled
						isLoading={isLoading}
						isOpenFilters={isOpenFilters}
						isPinColumns
						isWithoutCheckbox
						isWithoutDeleteAction
						pagination={pagination}
						search={search}
						sort={sort}
						stickyLeftColumnIndexes={[0, 1]}
					/>
				);
			case CHANGE_MONITORING_TABS_NAMES.changeLog:
				return (
					<Table
						key={CHANGE_MONITORING_TABS_NAMES.changeLog}
						columns={columns}
						data={changeMonitoringHistory}
						filters={filters}
						isDeleteActionDisabled
						isLoading={isLoading}
						isOpenFilters={isOpenFilters}
						isPinColumns
						isWithoutCheckbox
						isWithoutDeleteAction
						pagination={pagination}
						search={search}
						sort={sort}
					/>
				);
			default:
				return null;
		}
	}, [
		filters,
		currentTab,
		changeMonitoringActive,
		changeMonitoringHistory,
		isLoading,
		columns,
		pagination,
		sort,
		search,
		isOpenFilters,
		isViewerOnly,
		handleSendAllAcceptedToRunning,
		handleCancelAllErrors,
	]);

	return (
		<div className={styles.wrapper}>
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyNotesForm}
				isOpen={isOpenNoteModal}
				onClose={closeNoteModal}
				title='View comment to changes'
			>
				<NetworkConfigurationsNotes
					config={selectedConfiguration}
					isEditMode={isEditMode}
					onClose={closeNoteModal}
					setIsDirtyForm={setIsDirtyNotesForm}
				/>
			</Modal>
			{isOpenViewModal && selectedConfiguration
				&& <ViewChanges
					networkElement={selectedConfiguration}
					onClose={closeViewModal}
				/>
			}
			{isEditMode && selectedConfiguration && (
				<EditChanges
					networkElement={selectedConfiguration}
					onClose={closeEditModal}
				/>
			)}
			<Modal
				isLoading={isLoadingChangeMonitoringErrorMessage}
				isOpen={isErrorModal}
				onClose={closeErrorMessageModal}
				title='View error'
			>
				<Confirm
					containerClassName={styles.wordBreak}
					negativeBtnText='Close'
					onClose={closeErrorMessageModal}
					question={errorMessage}
				/>
			</Modal>
			<Modal
				isOpen={isStatusConfirmationOpen}
				onClose={closeStatusConfirmationModal}
				width={MODAL_WIDTH_580}
			>
				<Confirm
					isLoading={isLoadingConfirmation}
					negativeBtnText={confirmationProps?.negativeBtnText}
					onClose={closeStatusConfirmationModal}
					onProceed={confirmationProps?.onProceed as () => void}
					onSubmit={confirmationProps?.onSubmit}
					positiveBtnText={confirmationProps?.positiveBtnText}
					proceedBtnText={confirmationProps?.proceedBtnText}
					question={confirmationProps?.question}
				>
					{confirmationProps?.children}
				</Confirm>
			</Modal>
			{selectedConfiguration && (
				<Modal
					height='80%'
					isOpen={isHistoryOpen}
					isScrollableContent
					onClose={closeHistoryModal}
					title='History of statuses'
					width='80%'
				>
					<ChangeMonitoringHistoryStatuses
						name={selectedConfiguration.parameterName}
						updateId={selectedConfiguration.id}
						value={selectedConfiguration.parameterValue}
					/>
				</Modal>
			)}
			<Button
				className={styles.backBtn}
				icon={Icons.arrowLeft()}
				onClick={onBackClickHandler}
				text='Back'
				withoutBackground
			/>
			<div className={styles.header}>
				<div className={styles.title}>{changeMonitoringCard.networkElementName}</div>
				<div className={styles.detail}>
					<div className={styles.name}>Type:</div>
					<div className={styles.value}>{changeMonitoringCard.type?.title}</div>
				</div>
				<div className={styles.detail}>
					<div className={styles.name}>Middleware:</div>
					<div className={styles.value}>{changeMonitoringCard.middlewareName}</div>
				</div>
			</div>
			<ProfileTabSection
				containerClassname={styles.whiteWrapper}
				currentTab={currentTab}
				getCurrentTab={getCurrentTab}
				isFullHeight
				isWithoutShadow
				maxWidth='unset'
				setCurrentTab={setCurrentTab}
				tabs={CHANGE_MONITORING_TABS}
			/>
		</div>
	);
};

export default ChangeMonitoring;
